import React, { useState, useEffect } from 'react';
import { confirmDialog } from '../lib/confirm';
import { notifyError } from '../lib/notify';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from '../../config/axios';
import Layout, { TopBar } from '../components/Layout';
import { Button, Meta, Display, Chip } from '../components/Atoms';
import { LoadingState, ErrorState } from '../components/states';
import { useApi, formatDate } from '../lib/api';

/**
 * GardenPlanDetail - szczegół jednego planu ogrodniczego.
 *
 * Endpointy:
 *   GET /api/garden-plans/:id - plan + items
 *   POST /api/garden-plans/:id/items - dodaj pozycję
 *   DELETE /api/garden-plans/:id/items/:itemId - usuń
 *   PUT /api/garden-plans/:id - zmień status
 *   POST /api/garden-plans/:id/convert-to-tasks - utwórz zadania
 *   POST /api/garden-plans/:id/execute - utwórz grządki
 *   DELETE /api/garden-plans/:id - usuń plan
 */

const STATUS_LABEL = {
  draft: 'Szkic',
  active: 'Aktywny',
  completed: 'Ukończony',
  archived: 'Zarchiwizowany',
};

const STATUS_TONE = {
  draft: 'dim',
  active: 'sage',
  completed: 'amber',
  archived: 'dim',
};

// ── PlanCanvas — wizualny edytor pozycji X,Y items na siatce działki (M20) ──
//
// Renderuje prostokąt działki (width_cm × length_cm z planu albo default
// 800×400cm) ze skalą 0.5px/cm. Każdy item to przeciągany kafelek.
// PUT /api/garden-plans/:planId/items/:itemId po release.
function PlanCanvas({ planId, items, planWidthCm, planLengthCm, onItemMoved }) {
  const SCALE = 0.5; // 1cm = 0.5px (800×400cm = 400×200px)
  const W_CM = planWidthCm || 800;
  const L_CM = planLengthCm || 400;
  const wPx = W_CM * SCALE;
  const lPx = L_CM * SCALE;

  // Local pos state (optimistic — flush na backend onMouseUp)
  const [positions, setPositions] = React.useState(() => {
    const map = {};
    items.forEach(it => {
      map[it.id] = {
        x: it.position_x ?? 10,
        y: it.position_y ?? 10,
        w: it.width_cm ?? 60,
        h: it.height_cm ?? 60,
      };
    });
    return map;
  });

  // Re-sync gdy items zmienią się (dodanie/usunięcie z innej części UI)
  React.useEffect(() => {
    setPositions(prev => {
      const next = { ...prev };
      items.forEach(it => {
        if (!next[it.id]) {
          next[it.id] = {
            x: it.position_x ?? 10,
            y: it.position_y ?? 10,
            w: it.width_cm ?? 60,
            h: it.height_cm ?? 60,
          };
        }
      });
      // Usuń pozycje dla items które już nie istnieją
      Object.keys(next).forEach(id => {
        if (!items.find(it => String(it.id) === String(id))) delete next[id];
      });
      return next;
    });
  }, [items]);

  const [dragging, setDragging] = React.useState(null); // { id, startX, startY, origPos }
  const canvasRef = React.useRef(null);

  const handleMouseDown = (e, item) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging({
      id: item.id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      origPos: { ...positions[item.id] },
      canvasRect: rect,
    });
  };

  const handleMouseMove = React.useCallback((e) => {
    if (!dragging) return;
    e.preventDefault();
    const dx = (e.clientX - dragging.startMouseX) / SCALE; // px → cm
    const dy = (e.clientY - dragging.startMouseY) / SCALE;
    const newX = Math.max(0, Math.min(W_CM - dragging.origPos.w, dragging.origPos.x + dx));
    const newY = Math.max(0, Math.min(L_CM - dragging.origPos.h, dragging.origPos.y + dy));
    setPositions(prev => ({ ...prev, [dragging.id]: { ...prev[dragging.id], x: newX, y: newY } }));
  }, [dragging, W_CM, L_CM]);

  const handleMouseUp = React.useCallback(async () => {
    if (!dragging) return;
    const finalPos = positions[dragging.id];
    const startPos = dragging.origPos;
    setDragging(null);
    // Sprawdź czy faktycznie się zmieniło (>2cm) — żeby nie zalewać API
    if (Math.abs(finalPos.x - startPos.x) < 2 && Math.abs(finalPos.y - startPos.y) < 2) return;
    try {
      await axios.put(`/api/garden-plans/${planId}/items/${dragging.id}`, {
        position_x: Math.round(finalPos.x),
        position_y: Math.round(finalPos.y),
      });
      onItemMoved?.();
    } catch (err) {
      console.error('Drag save failed:', err);
      // Rollback
      setPositions(prev => ({ ...prev, [dragging.id]: startPos }));
    }
  }, [dragging, positions, planId, onItemMoved]);

  React.useEffect(() => {
    if (!dragging) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    // Touch fallback
    const touchMove = (e) => {
      if (e.touches[0]) handleMouseMove(e.touches[0]);
    };
    const touchEnd = handleMouseUp;
    window.addEventListener('touchmove', touchMove, { passive: false });
    window.addEventListener('touchend', touchEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', touchEnd);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  if (items.length === 0) return null;

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 10, marginBottom: 10,
      }}>
        <Meta>Wizualny układ działki ({W_CM} × {L_CM} cm)</Meta>
        <span style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.18em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>Skala 1:{Math.round(1 / SCALE * 100)}cm</span>
      </div>

      <div style={{
        padding: 16, borderRadius: 12,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        overflowX: 'auto',
      }}>
        <div style={{
          textAlign: 'center', marginBottom: 8,
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.22em', color: 'var(--t-text-mute)',
        }}>↑ N</div>

        <div
          ref={canvasRef}
          style={{
            position: 'relative',
            width: wPx, height: lPx,
            margin: '0 auto',
            background: `
              repeating-linear-gradient(0deg,
                transparent 0, transparent ${50 * SCALE - 1}px,
                rgba(232, 230, 221, 0.04) ${50 * SCALE - 1}px, rgba(232, 230, 221, 0.04) ${50 * SCALE}px),
              repeating-linear-gradient(90deg,
                transparent 0, transparent ${50 * SCALE - 1}px,
                rgba(232, 230, 221, 0.04) ${50 * SCALE - 1}px, rgba(232, 230, 221, 0.04) ${50 * SCALE}px),
              var(--t-bg)
            `,
            border: '1.5px dashed var(--t-line-2)',
            borderRadius: 8,
            userSelect: 'none',
            touchAction: 'none',
          }}
        >
          {items.map((item, idx) => {
            const pos = positions[item.id];
            if (!pos) return null;
            const colors = ['var(--t-sage)', 'var(--t-amber)', 'var(--t-rust)', 'var(--t-sage-deep)'];
            const c = colors[idx % colors.length];
            const isDragging = dragging?.id === item.id;

            return (
              <div
                key={item.id}
                onMouseDown={e => handleMouseDown(e, item)}
                onTouchStart={e => {
                  if (e.touches[0]) handleMouseDown(e.touches[0], item);
                }}
                style={{
                  position: 'absolute',
                  left: pos.x * SCALE,
                  top: pos.y * SCALE,
                  width: pos.w * SCALE,
                  height: pos.h * SCALE,
                  background: `${c}26`,
                  border: `2px solid ${c}`,
                  borderRadius: 6,
                  padding: 4,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  zIndex: isDragging ? 10 : 1,
                  boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.4)' : 'none',
                  transition: isDragging ? 'none' : 'box-shadow 200ms ease',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                  overflow: 'hidden',
                }}
                title={`${item.plant_name} — pozycja ${Math.round(pos.x)}×${Math.round(pos.y)}cm, rozmiar ${pos.w}×${pos.h}cm`}
              >
                <div style={{
                  fontSize: Math.max(8, Math.min(11, pos.w * SCALE / 8)),
                  color: c,
                  fontFamily: 'var(--t-sans)', fontWeight: 500,
                  letterSpacing: '-.01em',
                  textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
                  lineHeight: 1.1,
                }}>{item.plant_name}</div>
                <div style={{
                  fontFamily: 'var(--t-mono)', fontSize: 8,
                  color: c, opacity: 0.7,
                  letterSpacing: '.05em',
                }}>×{item.quantity || 1}</div>
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: 8, textAlign: 'center',
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.3em', color: 'var(--t-text-mute)',
        }}>──── OGRODZENIE ────</div>
      </div>

      <div style={{
        marginTop: 8, fontSize: 11, color: 'var(--t-text-mute)',
        textAlign: 'center', fontStyle: 'italic',
      }}>
        Przeciągnij kafelki aby zmienić pozycję. Zmiana zapisuje się automatycznie.
      </div>
    </div>
  );
}

function ItemRow({ item, onDelete }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 14px', borderRadius: 10,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      marginBottom: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
          marginBottom: 4,
        }}>
          <span style={{
            fontSize: 14, color: 'var(--t-text)',
            letterSpacing: '-.01em', fontWeight: 500,
          }}>{item.plant_name || item.display_name || '—'}</span>
          {item.quantity > 1 && (
            <span style={{
              fontFamily: 'var(--t-mono)', fontSize: 11,
              letterSpacing: '.12em', color: 'var(--t-amber)',
            }}>×{item.quantity}</span>
          )}
        </div>
        <div style={{
          display: 'flex', gap: 12, fontSize: 11,
          color: 'var(--t-text-mute)',
          fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        }}>
          {item.planned_date && (
            <span>SADZONE: {formatDate(item.planned_date).toUpperCase()}</span>
          )}
          {item.row_number && <span>· RZĄD {item.row_number}</span>}
          {item.spacing && <span>· {item.spacing}</span>}
        </div>
        {item.notes && (
          <div style={{
            fontSize: 12, color: 'var(--t-text-dim)',
            marginTop: 6, lineHeight: 1.45,
          }}>{item.notes}</div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDelete(item.id)}
        style={{
          background: 'transparent', border: 'none',
          color: 'var(--t-text-mute)', cursor: 'pointer',
          padding: 4, fontSize: 18, lineHeight: 1,
          flexShrink: 0,
        }}
        aria-label="Usuń pozycję"
        title="Usuń"
      >×</button>
    </div>
  );
}

function AddItemForm({ planId, items, onAdded }) {
  const [form, setForm] = useState({
    plant_name: '',
    quantity: 1,
    planned_date: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [companionData, setCompanionData] = useState(null);
  const [companionLoading, setCompanionLoading] = useState(false);

  // Debounced companion check: gdy użytkownik wpisuje nazwę, pobierz relacje
  useEffect(() => {
    const name = form.plant_name.trim().toLowerCase();
    if (!name || name.length < 2) { setCompanionData(null); return; }

    const existing = (items || []).map(it => it.plant_name).filter(Boolean);
    if (existing.length === 0) { setCompanionData(null); return; }

    const timer = setTimeout(async () => {
      setCompanionLoading(true);
      try {
        const res = await axios.get(`/api/plants/${encodeURIComponent(name)}/companions`);
        const { good, bad } = res.data;

        // Znajdź relacje z istniejącymi roślinami
        const rels = [];
        existing.forEach(plant => {
          const lo = plant.toLowerCase().trim();
          const g = good.find(g => g.name.toLowerCase() === lo);
          const b = bad.find(b => b.name.toLowerCase() === lo);
          if (g) rels.push({ plant: g.name, relation: 'good', reason: g.reason });
          if (b) rels.push({ plant: b.name, relation: 'bad', reason: b.reason });
        });
        setCompanionData(rels.length > 0 ? rels : []);
      } catch (e) {
        setCompanionData(null);
      } finally {
        setCompanionLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [form.plant_name, items]);

  const inputStyle = {
    padding: '10px 12px', borderRadius: 8,
    background: 'var(--t-bg)', border: '1px solid var(--t-line-2)',
    color: 'var(--t-text)', fontFamily: 'var(--t-sans)', fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
  };

  const handle = async (e) => {
    e?.preventDefault?.();
    if (!form.plant_name.trim()) {
      setErr('Podaj nazwę rośliny.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      await axios.post(`/api/garden-plans/${planId}/items`, {
        plant_name: form.plant_name.trim(),
        quantity: parseInt(form.quantity, 10) || 1,
        planned_date: form.planned_date || undefined,
        notes: form.notes.trim() || undefined,
      });
      setForm({ plant_name: '', quantity: 1, planned_date: '', notes: '' });
      onAdded?.();
    } catch (e) {
      setErr(e.response?.data?.error || 'Nie udało się dodać.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handle} style={{
      padding: '16px 18px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      marginBottom: 18,
    }}>
      <Meta style={{ marginBottom: 10 }}>Dodaj roślinę do planu</Meta>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr',
        gap: 10, marginBottom: 10,
      }}>
        <input
          type="text"
          aria-label="Nazwa rośliny do posadzenia"
          placeholder="Nazwa (np. pomidor Black Krim)"
          value={form.plant_name}
          onChange={e => setForm({ ...form, plant_name: e.target.value })}
          maxLength={100}
          style={inputStyle}
        />
        <input
          type="number"
          aria-label="Ilość"
          placeholder="Ilość"
          value={form.quantity}
          onChange={e => setForm({ ...form, quantity: e.target.value })}
          min={1}
          max={9999}
          style={{ ...inputStyle, fontFamily: 'var(--t-mono)', textAlign: 'right' }}
        />
        <input
          type="date"
          aria-label="Planowana data"
          value={form.planned_date}
          onChange={e => setForm({ ...form, planned_date: e.target.value })}
          style={{ ...inputStyle, colorScheme: 'dark' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <input
          type="text"
          aria-label="Notatka"
          placeholder="Notatka (opcjonalna)"
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          maxLength={300}
          style={{ ...inputStyle, flex: 1 }}
        />
        <Button variant="primary" type="submit" disabled={saving || !form.plant_name.trim()}>
          {saving ? '…' : '+ Dodaj'}
        </Button>
      </div>

      {/* Live companion feedback */}
      {companionLoading && (
        <div style={{
          marginTop: 8, fontSize: 11, color: 'var(--t-text-mute)',
          fontFamily: 'var(--t-mono)', letterSpacing: '.08em',
        }}>Sprawdzam sąsiedztwo…</div>
      )}
      {companionData && companionData.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {companionData.map((rel, i) => (
            <div key={i} style={{
              fontSize: 12, lineHeight: 1.5, marginBottom: 4,
              padding: '6px 10px', borderRadius: 6,
              background: rel.relation === 'bad'
                ? 'rgba(192, 106, 74, 0.08)'
                : 'rgba(168, 196, 158, 0.08)',
              borderLeft: `3px solid ${
                rel.relation === 'bad' ? 'var(--t-rust)' : 'var(--t-sage)'
              }`,
              color: rel.relation === 'bad' ? 'var(--t-rust)' : 'var(--t-sage)',
            }}>
              {rel.relation === 'bad' ? '⚠' : '✓'} <strong>{rel.plant}</strong>
              {rel.reason ? ` — ${rel.reason}` : ` (${rel.relation === 'bad' ? 'unikać' : 'dobry'})`}
            </div>
          ))}
        </div>
      )}
      {companionData && companionData.length === 0 && form.plant_name.trim().length >= 2 && (
        <div style={{
          marginTop: 8, fontSize: 11, color: 'var(--t-text-mute)',
          fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        }}>Brak znanych relacji sąsiedztwa z istniejącymi roślinami.</div>
      )}

      {err && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--t-rust)' }}>{err}</div>
      )}
    </form>
  );
}

// ── CompanionAnalysisCard — live analiza towarzystwa wszystkich items planu ──
function CompanionAnalysisCard({ planId, itemsCount, refreshTrigger }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (itemsCount === 0) { setData(null); return; }
    setLoading(true); setErr(null);
    axios.get(`/api/garden-plans/${planId}/companion-analysis`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [planId, itemsCount, refreshTrigger]);

  if (itemsCount === 0) return null;
  if (loading) {
    return (
      <div style={{
        padding: '14px 16px', borderRadius: 12,
        background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
        marginBottom: 18, fontSize: 13, color: 'var(--t-text-dim)',
      }}>Analizuję sąsiedztwo roślin…</div>
    );
  }
  if (err || !data) return null;

  const conflicts = data.conflicts || [];
  const benefits = data.benefits || [];
  const isClean = conflicts.length === 0 && benefits.length === 0;

  return (
    <div style={{
      padding: 16, borderRadius: 12,
      background: conflicts.length > 0
        ? 'rgba(192, 106, 74, 0.06)'
        : benefits.length > 0
        ? 'rgba(168, 196, 158, 0.06)'
        : 'var(--t-bg-elev)',
      border: `1px solid ${
        conflicts.length > 0 ? 'rgba(192, 106, 74, 0.30)'
        : benefits.length > 0 ? 'rgba(168, 196, 158, 0.30)'
        : 'var(--t-line)'
      }`,
      marginBottom: 22,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 10, marginBottom: 12,
      }}>
        <Meta color={conflicts.length > 0 ? 'rust' : 'sage'}>
          {conflicts.length > 0 ? '⚠' : '✓'} Analiza sąsiedztwa roślin
        </Meta>
        <span style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.18em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>{data.total_plants} roślin</span>
      </div>

      {isClean && (
        <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.55 }}>
          Wszystkie rośliny w planie są neutralne wobec siebie — żadnych konfliktów,
          ale też żadnych szczególnych korzyści z towarzystwa. Rozważ dodanie
          klasycznych par typu pomidor + bazylia czy marchew + cebula.
        </div>
      )}

      {conflicts.length > 0 && (
        <div style={{ marginBottom: benefits.length > 0 ? 14 : 0 }}>
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: 'var(--t-rust)', marginBottom: 8,
          }}>⚠ Konflikty ({conflicts.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {conflicts.map((c, i) => (
              <div key={i} style={{
                fontSize: 13, color: 'var(--t-text)', lineHeight: 1.5,
                padding: '8px 12px', borderRadius: 8,
                background: 'rgba(192, 106, 74, 0.08)',
                borderLeft: '3px solid var(--t-rust)',
              }}>
                <strong>{c.plant1}</strong> ↔ <strong>{c.plant2}</strong> — {c.reason}
              </div>
            ))}
          </div>
        </div>
      )}

      {benefits.length > 0 && (
        <div>
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: 'var(--t-sage)', marginBottom: 8,
          }}>✓ Dobre sąsiedztwo ({benefits.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {benefits.slice(0, 5).map((b, i) => (
              <div key={i} style={{
                fontSize: 13, color: 'var(--t-text)', lineHeight: 1.5,
                padding: '8px 12px', borderRadius: 8,
                background: 'rgba(168, 196, 158, 0.08)',
                borderLeft: '3px solid var(--t-sage)',
              }}>
                <strong>{b.plant1}</strong> ↔ <strong>{b.plant2}</strong> — {b.reason}
              </div>
            ))}
            {benefits.length > 5 && (
              <div style={{
                fontSize: 12, color: 'var(--t-text-mute)', fontStyle: 'italic',
                textAlign: 'center', padding: '4px 0',
              }}>+ {benefits.length - 5} więcej dobrych par</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GardenPlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: plan, loading, error, refetch } = useApi(`/api/garden-plans/${id}`);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  if (error?.status === 401) {
    return <Layout><ErrorState error={error} /></Layout>;
  }
  if (error?.status === 404) {
    return (
      <Layout>
        <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 32px', textAlign: 'center' }}>
          <Display size={32}>Plan nie istnieje</Display>
          <div style={{ fontSize: 14, color: 'var(--t-text-dim)', marginTop: 12, marginBottom: 24 }}>
            Mógł zostać usunięty albo nie masz do niego dostępu.
          </div>
          <Link to="/garden-planner" style={{ textDecoration: 'none' }}>
            <Button variant="primary">← Wróć do listy planów</Button>
          </Link>
        </div>
      </Layout>
    );
  }
  if (loading) return <Layout><LoadingState /></Layout>;

  const items = plan?.items || [];

  const handleDeleteItem = async (itemId) => {
    if (!await confirmDialog('Usunąć tę pozycję z planu?')) return;
    try {
      await axios.delete(`/api/garden-plans/${id}/items/${itemId}`);
      refetch();
    } catch (err) {
      notifyError('Nie udało się usunąć: ' + (err.response?.data?.error || ''));
    }
  };

  const handleStatusChange = async (newStatus) => {
    setBusy(true);
    try {
      await axios.put(`/api/garden-plans/${id}`, { status: newStatus });
      refetch();
      setStatusMsg({ type: 'ok', text: `Status zmieniony na: ${STATUS_LABEL[newStatus]}` });
    } catch (err) {
      setStatusMsg({ type: 'err', text: 'Nie udało się zmienić statusu.' });
    } finally {
      setBusy(false);
      setTimeout(() => setStatusMsg(null), 3500);
    }
  };

  const handleConvertToTasks = async () => {
    if (!await confirmDialog(`Utworzyć zadania z ${items.length} pozycji planu?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`/api/garden-plans/${id}/convert-to-tasks`);
      setStatusMsg({ type: 'ok', text: `Utworzono ${res.data?.tasks_created || items.length} zadań.` });
      refetch();
    } catch (err) {
      setStatusMsg({ type: 'err', text: err.response?.data?.error || 'Nie udało się utworzyć zadań.' });
    } finally {
      setBusy(false);
      setTimeout(() => setStatusMsg(null), 5000);
    }
  };

  const handleExecutePlan = async () => {
    if (!await confirmDialog(
      `Wykonać plan? Zostaną utworzone REALNE grządki z ${items.length} pozycji ` +
      `w przypisanej działce. Akcja nieodwracalna — sprawdź pozycje przed potwierdzeniem.`
    )) return;
    setBusy(true);
    try {
      const res = await axios.post(`/api/garden-plans/${id}/execute`);
      const created = res.data?.beds_created || res.data?.created || items.length;
      setStatusMsg({
        type: 'ok',
        text: `✓ Plan wykonany: utworzono ${created} ${created === 1 ? 'grządkę' : created < 5 ? 'grządki' : 'grządek'}.`,
      });
      refetch();
    } catch (err) {
      setStatusMsg({
        type: 'err',
        text: err.response?.data?.error || 'Nie udało się wykonać planu (sprawdź czy plan ma przypisaną działkę).',
      });
    } finally {
      setBusy(false);
      setTimeout(() => setStatusMsg(null), 6000);
    }
  };

  const handleDeletePlan = async () => {
    if (!await confirmDialog('Usunąć cały plan razem ze wszystkimi pozycjami? Nieodwracalne.')) return;
    try {
      await axios.delete(`/api/garden-plans/${id}`);
      navigate('/garden-planner');
    } catch (err) {
      notifyError('Nie udało się usunąć planu.');
    }
  };

  return (
    <Layout topBar={<TopBar left={
      <Link to="/garden-planner" style={{
        fontFamily: 'var(--t-mono)', fontSize: 11,
        letterSpacing: '.18em', textTransform: 'uppercase',
        color: 'var(--t-sage)', textDecoration: 'none',
      }}>← Plany</Link>
    } />}>
      <div style={{
        maxWidth: 880, margin: '0 auto',
        padding: '40px 32px 60px',
      }}>
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12,
        }}>
          <Chip tone={STATUS_TONE[plan.status] || 'dim'}>
            {STATUS_LABEL[plan.status] || plan.status}
          </Chip>
          {plan.year && (
            <span style={{
              fontFamily: 'var(--t-mono)', fontSize: 11,
              letterSpacing: '.16em', color: 'var(--t-text-mute)',
            }}>{plan.year}</span>
          )}
          {plan.plot_name && (
            <span style={{ fontSize: 13, color: 'var(--t-text-dim)' }}>
              · {plan.plot_name}
            </span>
          )}
        </div>

        <Display size={'clamp(36px, 6vw, 48px)'} style={{
          display: 'block', letterSpacing: '-.03em', lineHeight: 1,
          marginBottom: 14,
        }}>{plan.name}</Display>

        {plan.description && (
          <div style={{
            fontSize: 15, color: 'var(--t-text-dim)',
            lineHeight: 1.55, marginBottom: 28, maxWidth: 600,
          }}>{plan.description}</div>
        )}

        {statusMsg && (
          <div style={{
            padding: '12px 14px', borderRadius: 10,
            background: statusMsg.type === 'ok'
              ? 'rgba(168, 196, 158, 0.10)'
              : 'rgba(192, 106, 74, 0.08)',
            borderLeft: `3px solid var(--t-${statusMsg.type === 'ok' ? 'sage' : 'rust'})`,
            fontSize: 13, color: 'var(--t-text)', marginBottom: 18,
          }}>{statusMsg.text}</div>
        )}

        {/* Akcje na planie */}
        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap',
          padding: '14px 16px', borderRadius: 12,
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
          marginBottom: 28,
        }}>
          {plan.status === 'draft' && (
            <Button variant="primary" disabled={busy}
              onClick={() => handleStatusChange('active')}>
              ▶ Aktywuj plan
            </Button>
          )}
          {plan.status === 'active' && (
            <>
              <Button variant="primary" disabled={busy || items.length === 0}
                onClick={handleConvertToTasks}>
                Utwórz zadania ({items.length})
              </Button>
              <Button variant="ghost" disabled={busy || items.length === 0 || !plan.plot_id}
                onClick={handleExecutePlan}
                title={!plan.plot_id ? 'Plan nie ma przypisanej działki — najpierw przypisz' : ''}>
                ▷ Wykonaj plan (utwórz grządki)
              </Button>
              <Button variant="ghost" disabled={busy}
                onClick={() => handleStatusChange('completed')}>
                ✓ Oznacz jako ukończone
              </Button>
            </>
          )}
          {(plan.status === 'completed' || plan.status === 'archived') && (
            <Button variant="ghost" disabled={busy}
              onClick={() => handleStatusChange('active')}>
              Przywróć do aktywnych
            </Button>
          )}
          <Button variant="ghost" onClick={() => window.print()} disabled={items.length === 0}>
            🖨 Drukuj plan
          </Button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={handleDeletePlan}
            style={{
              padding: '10px 16px', borderRadius: 10,
              background: 'transparent',
              border: '1px solid var(--t-rust)',
              color: 'var(--t-rust)',
              fontFamily: 'var(--t-sans)', fontSize: 13,
              cursor: 'pointer',
            }}
          >Usuń plan</button>
        </div>

        <AddItemForm planId={id} items={items} onAdded={refetch} />

        <PlanCanvas
          planId={id}
          items={items}
          planWidthCm={plan.width_cm}
          planLengthCm={plan.length_cm}
          onItemMoved={refetch}
        />

        <CompanionAnalysisCard
          planId={id}
          itemsCount={items.length}
          refreshTrigger={items.length}
        />

        <Meta style={{ marginBottom: 12 }}>
          Pozycje planu ({items.length})
        </Meta>

        {items.length > 0 ? (
          <div>
            {items.map(item => (
              <ItemRow key={item.id} item={item} onDelete={handleDeleteItem} />
            ))}
          </div>
        ) : (
          <div style={{
            padding: '40px 24px', textAlign: 'center',
            border: '1px dashed var(--t-line-2)', borderRadius: 12,
            color: 'var(--t-text-mute)', fontSize: 14,
            fontStyle: 'italic',
          }}>↳ Brak pozycji. Dodaj pierwszą roślinę nad listą.</div>
        )}
      </div>
    </Layout>
  );
}
